import {useState, useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {pushSnackAlert} from '@optimaxdev/design-system';

import {TYP_RX_DETAILS, TYP_TYP_FORM_ACTION} from 'constants/analytics/events/typ';
import {ENDPOINTS} from 'constants/endpoints';
import {
    WE_GOT_IT,
    WE_WILL_VERIFY_THE_PRESCRIPTION,
} from 'desktop/pages/success/store/reducers/orderItems/constants';
import {
    DoctorDetailsAndUploadValidatedType,
    SuccessScreenType,
} from 'desktop/pages/success/types/orderItemsTypes';
import {apiConfig} from 'libs/api/api';
import {FieldType} from 'types/successPage';

type StateType = {
    error: string;
    isLoading: boolean;
};

export type PropsType = {
    items: Array<FieldType>;
    orderId: string;
    setErrors: (data: DoctorDetailsAndUploadValidatedType) => void;
    successPageItemsValidated: {
        items: DoctorDetailsAndUploadValidatedType;
        isAllValid: boolean;
    };
    message: string;
    changeScreen: (screen: SuccessScreenType) => void;
    hasOnlyDoctorDetails: boolean;
    label: string;
};

/**
 * Special type for send to our backend
 */
type BackendType = {
    [key: number]: {
        file?: string;
        doctorDetails?: Pick<FieldType, 'doctorDetails'>;
    };
};

/**
 * This manipulation to reformat data is requested by Vadim Meling according to comment in pull request
 * https://bitbucket.gusadev.com/projects/SRV/repos/magento/pull-requests/872/overview?commentId=61863
 * @param {Array<FieldType>} items - initial items
 * @returns {Object} with key orderItemId and data inside
 */
const convertDataValuesToBackendFormat = (items: Array<FieldType>): BackendType =>
    items.reduce(
        (acc, item) => ({
            ...acc,
            [item.orderItemId]: {
                ...(item.fileSrc ? {file: item.fileSrc} : {doctorDetails: item.doctorDetails}),
            },
        }),
        {},
    );

/**
 * sends analytics after confirm button click and successful response
 * @param {Array<FieldType>} items - initial items
 * @param {string | undefined} errorMessage - message with error info
 */
const sendSuccessConfirmDrDetailsAnalytics = (items: Array<FieldType>) => {
    items.forEach(item => {
        if ('isAutocompleteModeEnabled' in item) {
            sendDesktopAnalyticsEvent(TYP_TYP_FORM_ACTION, {
                label: `Confirm - Success - ${
                    item.isAutocompleteModeEnabled ? 'Via Smart Search' : 'Via Manual'
                }`,
                version: 'D',
            });
        }
    });
};

/**
 * sends error analytics after confirm button click
 * @param {Array<FieldType>} items - initial items
 * @param {string | undefined} errorMessage - message with error info
 */
const sendErrorConfirmDrDetailsAnalytics = (items: Array<FieldType>, error?: string) => {
    items.forEach(item => {
        if ('isAutocompleteModeEnabled' in item) {
            let errorMsg = item.isAutocompleteModeEnabled
                ? ' - Please select doctor or clinic from the list'
                : ' - Please fill in all fields';
            if (error) errorMsg = ` - ${error}`;
            sendDesktopAnalyticsEvent(TYP_TYP_FORM_ACTION, {
                label: `Confirm - Error - ${
                    item.isAutocompleteModeEnabled ? 'Via Smart Search' : 'Via Manual'
                }${errorMsg}`,
                version: 'D',
            });
        }
    });
};

/**
 * Displays sticky cta button to confirm your prescription on TYP
 * @constructor
 */
export const useConfirmButton = ({
    changeScreen,
    hasOnlyDoctorDetails,
    items,
    label,
    message,
    orderId,
    setErrors,
    successPageItemsValidated,
}: PropsType) => {
    const [state, setState] = useState<StateType>({
        error: '',
        isLoading: false,
    });

    useEffect(() => {
        if (!message) setState({error: message, isLoading: false});
    }, [message]);

    useEffect(() => {
        if (state.error) pushSnackAlert('Error', state.error);
    }, [state]);

    const invalidOptionHandler = () => {
        setErrors(successPageItemsValidated.items);
        setState({error: message, isLoading: false});
        sendErrorConfirmDrDetailsAnalytics(items);
    };

    const onClick = () => {
        if (!successPageItemsValidated.isAllValid) {
            invalidOptionHandler();
            return;
        }

        if (label)
            sendDesktopAnalyticsEvent(TYP_RX_DETAILS, {
                label,
            });

        setState({error: '', isLoading: true});
        setErrors({});
        apiConfig.instanceGusaBackend
            .put(`${ENDPOINTS.SUCCESS_PAGE_CONFIRM_ORDER({orderId})}`, {
                items: convertDataValuesToBackendFormat(items),
            })
            .then(() => {
                sendSuccessConfirmDrDetailsAnalytics(items);
                changeScreen(hasOnlyDoctorDetails ? WE_GOT_IT : WE_WILL_VERIFY_THE_PRESCRIPTION);
            })
            .catch(() => {
                const errorMessage = 'Some error occurred. Please try later.';
                sendErrorConfirmDrDetailsAnalytics(items, errorMessage);
                setState({error: errorMessage, isLoading: false});
            });
    };

    return {onClick, state};
};
